import {
  commentsSummaryResponseSchema,
  socialCommentDetailSchema,
  socialCommentListResponseSchema,
  socialCommentPrivateReplyRequestSchema,
  socialCommentPrivateReplyResponseSchema,
  socialCommentPublicReplyRequestSchema,
  socialCommentPublicReplyResponseSchema,
  socialCommentsSyncRequestSchema,
  socialCommentsSyncResponseSchema,
  socialCommentThreadResponseSchema,
  socialPostDetailSchema,
  socialPostListResponseSchema,
  updateSocialCommentHandlingStatusRequestSchema,
  type CommentsSummaryResponse,
  type SocialCommentDetailDto,
  type SocialCommentHandlingStatus,
  type SocialCommentHandlingStatusFilter,
  type SocialCommentListResponse,
  type SocialCommentPlatformFilter,
  type SocialCommentPrivateReplyResponse,
  type SocialCommentPublicReplyResponse,
  type SocialCommentsSyncRequest,
  type SocialCommentsSyncResponse,
  type SocialCommentThreadResponse,
  type SocialPostDetailDto,
  type SocialPostListResponse,
} from "@multi-chat/shared";
import { apiGet, apiPatch, apiPost } from "./client.js";

export const SOCIAL_COMMENT_PAGE_SIZE = 30;
export const SOCIAL_POST_PAGE_SIZE = 30;
export const SOCIAL_COMMENT_THREAD_PAGE_SIZE = 50;

export type SocialCommentListClientQuery = {
  platform?: SocialCommentPlatformFilter;
  platformEndpointId?: number;
  handlingStatus?: SocialCommentHandlingStatusFilter;
  postId?: string;
  search?: string;
  sort?: "activity_desc" | "activity_asc";
  cursor?: string;
  limit?: number;
  signal?: AbortSignal;
};

export type SocialPostListClientQuery = {
  platform?: SocialCommentPlatformFilter;
  platformEndpointId?: number;
  hasOpenComments?: boolean;
  search?: string;
  cursor?: string;
  limit?: number;
  signal?: AbortSignal;
};

export type SocialCommentThreadClientQuery = {
  scope?: "thread" | "all_post_comments";
  cursor?: string;
  limit?: number;
  signal?: AbortSignal;
};

function orgSocialCommentsBase(organizationId: number): string {
  return `/organizations/${organizationId}/social-comments`;
}

function withQuery(path: string, params: Record<string, string | undefined>): string {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value != null && value !== "") {
      query.set(key, value);
    }
  }
  const encoded = query.toString();
  return encoded.length > 0 ? `${path}?${encoded}` : path;
}

export async function getCommentsSummary(
  organizationId: number,
  signal?: AbortSignal,
): Promise<CommentsSummaryResponse> {
  return commentsSummaryResponseSchema.parse(
    await apiGet(`/organizations/${organizationId}/inbox/comments-summary`, signal),
  );
}

export async function listSocialComments(
  organizationId: number,
  params: SocialCommentListClientQuery = {},
): Promise<SocialCommentListResponse> {
  const { signal, ...query } = params;
  return socialCommentListResponseSchema.parse(
    await apiGet(
      withQuery(orgSocialCommentsBase(organizationId), {
        platform: query.platform,
        platformEndpointId:
          query.platformEndpointId != null ? String(query.platformEndpointId) : undefined,
        handlingStatus: query.handlingStatus,
        postId: query.postId,
        search: query.search,
        sort: query.sort,
        cursor: query.cursor,
        limit: query.limit != null ? String(query.limit) : undefined,
      }),
      signal,
    ),
  );
}

export async function listSocialPosts(
  organizationId: number,
  params: SocialPostListClientQuery = {},
): Promise<SocialPostListResponse> {
  const { signal, ...query } = params;
  return socialPostListResponseSchema.parse(
    await apiGet(
      withQuery(`/organizations/${organizationId}/social-posts`, {
        platform: query.platform,
        platformEndpointId:
          query.platformEndpointId != null ? String(query.platformEndpointId) : undefined,
        hasOpenComments:
          query.hasOpenComments == null ? undefined : query.hasOpenComments ? "true" : "false",
        search: query.search,
        cursor: query.cursor,
        limit: query.limit != null ? String(query.limit) : undefined,
      }),
      signal,
    ),
  );
}

export async function getSocialPost(
  organizationId: number,
  postId: string,
  signal?: AbortSignal,
): Promise<SocialPostDetailDto> {
  return socialPostDetailSchema.parse(
    await apiGet(`/organizations/${organizationId}/social-posts/${postId}`, signal),
  );
}

export async function getSocialCommentThread(
  organizationId: number,
  commentId: string,
  params: SocialCommentThreadClientQuery = {},
): Promise<SocialCommentThreadResponse> {
  const { signal, ...query } = params;
  return socialCommentThreadResponseSchema.parse(
    await apiGet(
      withQuery(`${orgSocialCommentsBase(organizationId)}/${commentId}/thread`, {
        scope: query.scope,
        cursor: query.cursor,
        limit: query.limit != null ? String(query.limit) : undefined,
      }),
      signal,
    ),
  );
}

export async function updateSocialCommentHandlingStatus(
  organizationId: number,
  commentId: string,
  handlingStatus: SocialCommentHandlingStatus,
  signal?: AbortSignal,
): Promise<SocialCommentDetailDto> {
  const payload = updateSocialCommentHandlingStatusRequestSchema.parse({ handlingStatus });
  return socialCommentDetailSchema.parse(
    await apiPatch(
      `${orgSocialCommentsBase(organizationId)}/${commentId}/handling-status`,
      payload,
      signal,
    ),
  );
}

export async function markSocialCommentRead(
  organizationId: number,
  commentId: string,
  signal?: AbortSignal,
): Promise<SocialCommentDetailDto> {
  return socialCommentDetailSchema.parse(
    await apiPost(`${orgSocialCommentsBase(organizationId)}/${commentId}/read`, undefined, signal),
  );
}

export async function syncSocialComments(
  organizationId: number,
  body: SocialCommentsSyncRequest = {},
  signal?: AbortSignal,
): Promise<SocialCommentsSyncResponse> {
  const payload = socialCommentsSyncRequestSchema.parse(body);
  return socialCommentsSyncResponseSchema.parse(
    await apiPost(`${orgSocialCommentsBase(organizationId)}/sync`, payload, signal),
  );
}

export async function sendSocialCommentPublicReply(
  organizationId: number,
  commentId: string,
  body: { content: string; clientIdempotencyKey: string; markHandled?: boolean },
  signal?: AbortSignal,
): Promise<SocialCommentPublicReplyResponse> {
  const payload = socialCommentPublicReplyRequestSchema.parse(body);
  return socialCommentPublicReplyResponseSchema.parse(
    await apiPost(
      `${orgSocialCommentsBase(organizationId)}/${commentId}/public-reply`,
      payload,
      signal,
    ),
  );
}

export async function sendSocialCommentPrivateReply(
  organizationId: number,
  commentId: string,
  body: { content: string; clientIdempotencyKey: string; markHandled?: boolean },
  signal?: AbortSignal,
): Promise<SocialCommentPrivateReplyResponse> {
  const payload = socialCommentPrivateReplyRequestSchema.parse(body);
  return socialCommentPrivateReplyResponseSchema.parse(
    await apiPost(
      `${orgSocialCommentsBase(organizationId)}/${commentId}/private-reply`,
      payload,
      signal,
    ),
  );
}
